import React, { useState, useEffect } from "react";
import { Eye, Heart, Folder, Trophy } from "lucide-react";
import { AnalyticsDashboardChart } from "./AnalyticsDashboardChart";
import { DateRangeDropdownV2 } from "@/components/ui/DateRangeDropdownV2";
import { useAuth } from "@/hooks/domains/auth/useAuth";
import { useUserProfileAnalytics } from "@/hooks/analytics/useUserProfileAnalytics";
import { useUserProfileStats } from '@/hooks/analytics/useUserProfileStats';
import { useProfileContext } from '@/contexts/ProfileContext';
import { usePortfolioLeaderboard } from '@/hooks/portfolio/usePortfolioLeaderboard';
import { useDashboardDateRange } from '@/contexts/DashboardContext';
import { useUnifiedPortfolioQuery } from '@/hooks/portfolio/useUnifiedPortfolioQuery';

const mockLeaderboard = [
  { name: "Logofolio", images: 8, views: 710, percent: 38 },
  { name: "Logofolio", images: 8, views: 473, percent: 26 },
  { name: "Logofolio", images: 8, views: 149, percent: 8 },
  { name: "Logofolio", images: 8, views: 132, percent: 7 },
  { name: "Logofolio", images: 8, views: 73, percent: 6 },
];

const AdvancedOverview: React.FC = () => {
  const { user } = useAuth();
  const [isMobile, setIsMobile] = useState(false);
  
  // Use date range from dashboard context for persistence
  const { dateRange, setDateRange } = useDashboardDateRange();
  
  const { profile, isLoading: profileLoading } = useProfileContext();
  const userId = profile?.id;
  const { data: unifiedItems = [], isLoading: portfolioLoading } = useUnifiedPortfolioQuery({
    profileId: userId,
    includeDrafts: true,
    enabled: !!userId
  });
  const { data: stats, isLoading: statsLoading, error } = useUserProfileStats(userId);
  const { data: leaderboard, isLoading: leaderboardLoading } = usePortfolioLeaderboard(userId);

  // Check if we're on mobile for responsive behavior
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Calculate total images from unified portfolio items
  let totalImages = 0;
  if (unifiedItems && Array.isArray(unifiedItems)) {
    totalImages = unifiedItems.reduce((sum, item) => {
      if (item && Array.isArray(item.images)) {
        return sum + item.images.length;
      }
      return sum;
    }, 0);
  }

  const { data: analyticsData, isLoading: analyticsLoading } = useUserProfileAnalytics(
    user?.id,
    dateRange.from!,
    dateRange.to!
  );

  if (profileLoading || !userId || statsLoading || portfolioLoading) {
    return <div>Loading...</div>;
  }
  if (error) {
    return <div>Error loading stats: {error.message}</div>;
  }

  return (
    <div className="bg-white rounded-lg shadow p-4 md:p-5 w-full">
      <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="font-medium text-base text-black mb-2 md:mb-0">Overview</div>
        <DateRangeDropdownV2
          value={dateRange}
          onChange={range => setDateRange({ from: range.from, to: range.to || range.from })}
        />
      </div>
      {/* Top Stats Row */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <StatCard
          label="Profile Views"
          value={stats?.total_views ?? 0}
          icon={<Eye className="h-5 w-5 text-gray-400" />}
        />
        <StatCard
          label="Profile Likes"
          value={stats?.total_likes ?? 0}
          icon={<Heart className="h-5 w-5 text-gray-400" />}
        />
        <StatCard
          label="Portfolio Projects"
          value={
            <span>
              <span className="font-extrabold text-2xl text-black">{stats?.total_projects ?? 0}</span>
              <span className="text-gray-400 font-normal text-base ml-1">/ {totalImages} images</span>
            </span>
          }
          icon={<Folder className="h-5 w-5 text-gray-400" />}
        />
      </div>
      <div className="border-b border-gray-200 mb-6" />
      {/* Analytics Dashboard */}
      <div className="mb-6">
        {analyticsLoading ? (
          <div className="h-64 flex items-center justify-center text-gray-400">Loading analytics...</div>
        ) : (
          <AnalyticsDashboardChart data={analyticsData || []} />
        )}
      </div>
      <div className="border-b border-gray-200 mb-6" />
      {/* Portfolio Views Leaderboard */}
      <div className="bg-white">
        <div className="flex items-center mb-4">
          <span className="font-bold text-lg text-black">Portfolio Views</span>
        </div>
        <div>
          {leaderboardLoading ? (
            <div>Loading leaderboard...</div>
          ) : (
            (Array.isArray(leaderboard) ? leaderboard : []).map((item, idx) => (
              <div key={item.portfolio_item_id} className="flex flex-col sm:flex-row sm:items-center justify-between py-3 border-b last:border-b-0 border-gray-100 gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-start gap-2">
                    <span className="font-semibold text-gray-700 flex-shrink-0">{idx + 1}.</span>
                    <div className="min-w-0 flex-1">
                      <div className="text-black/70 font-medium flex items-center gap-2">
                        <span className="truncate">{item.title}</span>
                        <span className="text-gray-500 text-sm flex-shrink-0">({item.images} {item.images === 1 ? 'image' : 'images'})</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0 w-full sm:w-auto">
                  <span className="text-gray-400 font-semibold text-sm sm:text-base min-w-[2rem] text-right">{item.views}</span>
                  <div className="flex-1 sm:flex-none sm:w-20 h-2 sm:h-3 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full" style={{ background: '#f59e42', width: `${item.views / ((leaderboard && leaderboard[0]?.views) || 1) * 100}%` }}></div>
                  </div>
                  <span className="text-gray-500 text-sm sm:text-base min-w-[2rem] text-right">
                    {Math.round((item.views / ((leaderboard && leaderboard[0]?.views) || 1)) * 100)}%
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

const StatCard: React.FC<{ label: string; value: React.ReactNode; icon: React.ReactNode }> = ({ label, value, icon }) => (
  <div className="flex-1 min-w-0 bg-white border border-gray-200 rounded-xl flex flex-col justify-between p-4 sm:p-5 h-[90px] sm:h-[100px]">
    <div className="flex items-center justify-between mb-2">
      <span className="text-xs sm:text-sm font-bold text-gray-700 truncate">{label}</span>
      <span className="flex-shrink-0">{icon}</span>
    </div>
    <div className="flex items-end h-full">
      <span className="font-extrabold text-2xl sm:text-3xl text-black">{typeof value === 'number' ? value : value}</span>
    </div>
  </div>
);

export default AdvancedOverview; 